import { EditorState, convertFromRaw, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import React from 'react';
import { useSelector } from 'react-redux';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { UnknownIntent } from '@debbie/common/dist/intents/index.js';
import { Lang } from '@debbie/common/dist/lang/Lang.js';
import { TemplateState, TemplateType, } from '@debbie/common/dist/templates/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import * as types from '@debbie/cortex/dist/newInteractions/types.js';
import { selectTemplates } from '@debbie/cortex/dist/templates/selectors.js';
import { SearchInput } from '..//index.js';
import { encodeText } from '../../utils/templates.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
const TemplateSearch = (props) => {
    const singleCase = props.case;
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const templates = useSelector(selectTemplates);
    const lang = useSelector((s) => s.lang);
    const tenant = useSelector(selectTenant);
    const contactPerson = getContactPerson(props.customer);
    const isTemplateForCase = (template, singleCase) => {
        if (!collector)
            return false;
        const caseCollectorsId = collector.collectorId;
        const isCreditorSame = template.creditorId === singleCase.creditorId || !template.creditorId;
        const isCollectorSame = caseCollectorsId === template.collectorId || !template.collectorId;
        const isAlternativeContact = !!(contactPerson && contactPerson.relation === 'alternative-contact');
        return (isCreditorSame &&
            isCollectorSame &&
            isAlternativeContact === template.isForAlternativeContact);
    };
    const mentionSuggestions = getMentionSuggestions(lang, props.templateType, propertyTypes || undefined, tenant);
    const selectTemplate = (template) => {
        const bodyContentBlock = htmlToDraft.default(encodeText(template.html || '', mentionSuggestions));
        const subjectContentBlock = htmlToDraft.default(encodeText(template.subject || '', mentionSuggestions));
        const newEditorState = template.html
            ? EditorState.createWithContent(ContentState.createFromBlockArray(bodyContentBlock.contentBlocks))
            : EditorState.createWithContent(convertFromRaw(types[`${props.templateType}InitialState`].editorState));
        const newSubjectEditorState = template.subject
            ? EditorState.createWithContent(ContentState.createFromBlockArray(subjectContentBlock.contentBlocks))
            : EditorState.createWithContent(convertFromRaw(types.emailInitialState.subjectEditorState));
        const templateData = {
            editorState: newEditorState,
            templateName: template.name,
            templateId: template.templateId,
            templateFileName: template.fileName,
            intent: template.intent,
            actions: template.details.actions,
        };
        c.newInteractions.selectTemplate(singleCase.caseId, props.templateType, [TemplateType.EMAIL, TemplateType.EBOKS].includes(props.templateType)
            ? { ...templateData, subjectEditorState: newSubjectEditorState }
            : templateData);
        if (props.onChange) {
            props.onChange(newEditorState, [TemplateType.EMAIL, TemplateType.EBOKS].includes(props.templateType)
                ? newSubjectEditorState
                : null);
        }
    };
    const removeTemplate = () => {
        const newEditorState = EditorState.createWithContent(convertFromRaw(types[`${props.templateType}InitialState`].editorState));
        const newSubjectEditorState = EditorState.createWithContent(convertFromRaw(types.emailInitialState.subjectEditorState));
        const templateData = {
            editorState: newEditorState,
            templateName: '',
            templateId: '',
            templateFileName: '',
            intent: UnknownIntent(),
            details: {},
        };
        c.newInteractions.selectTemplate(singleCase.caseId, props.templateType, props.templateType === TemplateType.EMAIL
            ? { ...templateData, subjectEditorState: newSubjectEditorState }
            : templateData);
        if (props.onChange) {
            props.onChange(newEditorState, props.templateType === TemplateType.EMAIL
                ? newSubjectEditorState
                : null);
        }
    };
    const searchAllTemplates = (searchPhrase) => {
        const DEFAULT_LANG = Lang.da; // TODO: Should come from tenant
        const preferredLang = contactPerson?.lang || DEFAULT_LANG;
        if (!templates)
            return [];
        const matchingTemplates = templates.filter((template) => {
            const isForCase = isTemplateForCase(template, singleCase);
            const containsSearchPhrase = searchPhrase.length === 0 ||
                template.name.toLowerCase().indexOf(searchPhrase.toLowerCase()) !== -1;
            const isCorrectType = template.type === props.templateType &&
                template.state === TemplateState.PUBLISHED;
            return isForCase && containsSearchPhrase && isCorrectType;
        });
        const languageTemplates = matchingTemplates.filter((template) => {
            // if template is preferred language return true
            if (template.lang === preferredLang)
                return true;
            // if template is not preferred language, but the preferred language exists return false
            if (template.lang !== preferredLang &&
                matchingTemplates.find((t) => t.templateId === template.templateId && t.lang === preferredLang))
                return false;
            // if template is not preferred language, and the preferred language does not exist return true
            return true;
        });
        return languageTemplates.map((template) => ({
            key: template.templateId,
            heading: template.name,
            subheading: template.isForAlternativeContact
                ? lang.debtors.alternativeContact
                : '',
            returnData: template,
        }));
    };
    return (React.createElement(SearchInput, { onSelect: selectTemplate, static: props.interaction.templateName, searchDelay: 0, forceOpen: true, searchAll: searchAllTemplates, placeholder: lang.interactions.selectTemplate, hasClearButton: !!props.interaction.templateId, onClear: removeTemplate }));
};
export default TemplateSearch;
//# sourceMappingURL=TemplateSearch.js.map